// ============================================================
// Viashift — Review queue data layer (Phase 3, Epic 3A.6)
// ============================================================
// Read + write helpers backing the /review queue list and detail
// pages. Sits on top of the inbound_emails table and the
// match_and_dispatch_parsed_email RPC; no business logic lives
// here beyond filter assembly and JSON shape massaging.
//
// Note on types: `database.types.ts` was regenerated 2026-06-29 and
// now includes `inbound_emails` and the other tables this module
// touches, so the client is fully typed (no `as any` boundary cast).
// JSON-column writes (corrected_payload) and the hand-declared
// ReviewQueueRow subset cross the jsonb/Json seam via narrow casts at
// those specific points; everything else is statically typed.
// ============================================================

import { supabase } from '@/lib/supabase';
import type { ClinicRole } from '@/lib/session';
import type { Json } from '@/lib/database.types';

// Typed client. The tables this module reads (inbound_emails, orders)
// and any RPCs it calls are present in the generated database.types as
// of the 2026-06-29 regen, so no `as any` boundary cast is needed.
const sb = supabase;

// ---- Parsed payload shape -----------------------------------------
//
// Loosely typed because the parser writes whatever Anthropic returns
// and we tolerate shape drift. Reviewer edits go in via the same
// shape. Consumers read defensively and never assume any leaf is
// present.

export interface ParsedLeaf<T = unknown> {
  value: T;
  confidence: number;
}

export interface ParsedLineItem {
  name?: ParsedLeaf<string>;
  qty?: ParsedLeaf<number>;
  unit_price?: ParsedLeaf<number>;
  uom?: ParsedLeaf<string>;
  gtin?: ParsedLeaf<string>;
  vendor_sku?: ParsedLeaf<string>;
  customer_sku?: ParsedLeaf<string>;
}

export interface ParsedPayload {
  vendor?: ParsedLeaf<string>;
  email_type?: ParsedLeaf<string>;
  order_references?: string[];
  ordered_at?: ParsedLeaf<string>;
  total?: ParsedLeaf<number>;
  line_items?: ParsedLineItem[];
  shipping?: Record<string, ParsedLeaf>;
  // Forward-compat: parser may add fields. Don't reject unknown keys.
  [k: string]: unknown;
}

// ---- Inbound email row shape (what the queue consumes) ------------

/** Distinct from the parse_status / dispatch_status enums on the
 *  table; this is the derived UI category used for chip filtering. */
export type ReviewCategory =
  | 'needs_match'
  | 'needs_review'
  | 'failed'
  | 'type_tagged'
  | 'quarantined';

export interface ReviewQueueRow {
  id: string;
  clinic_id: string;
  from_address: string;
  from_name: string | null;
  subject: string | null;
  parsed_at: string | null;
  parse_status: string;
  parse_error: string | null;
  dispatch_status: string;
  dispatch_result: unknown | null;
  parsed_payload: ParsedPayload | null;
  corrected_payload: ParsedPayload | null;
  review_dismissed_at: string | null;
  status: string;
}

export interface ReviewQueueDetail extends ReviewQueueRow {
  to_address: string;
  to_local_part: string;
  received_at: string;
  raw_text: string | null;
  raw_html: string | null;
  attachments_json: unknown | null;
  parse_model: string | null;
  dispatched_at: string | null;
}

// Columns shared by list + detail queries. Detail extends with the
// raw email body / headers / attachment metadata.
const LIST_COLUMNS =
  'id, clinic_id, from_address, from_name, subject, parsed_at, ' +
  'parse_status, parse_error, dispatch_status, dispatch_result, ' +
  'parsed_payload, corrected_payload, review_dismissed_at, status';

const DETAIL_COLUMNS =
  LIST_COLUMNS +
  ', to_address, to_local_part, received_at, raw_text, raw_html, ' +
  'attachments_json, parse_model, dispatched_at';

// ---- Filter assembly ----------------------------------------------

export interface QueueFilters {
  /** Status chip selection. null = "All" (every queue-eligible row). */
  category: ReviewCategory | null;
  /** Vendor name filter — exact match against parsed_payload.vendor.value.
   *  null = no vendor filter. */
  vendor: string | null;
}

/**
 * Derive a row's UI category from its parse / dispatch state.
 * Used both by the list query (for chip filter mapping) and by
 * row rendering (for chip display).
 *
 * Precedence: parse failures show as 'failed' regardless of
 * dispatch_status (which will be 'skipped' for those rows).
 * Otherwise dispatch_status wins.
 */
export function categorize(row: {
  parse_status: string;
  dispatch_status: string;
  status?: string;
}): ReviewCategory | null {
  // Quarantined (#2, Task 5) takes precedence — a quarantined email
  // never reached the parser, so parse_status/dispatch_status are at
  // their initial values and don't describe it.
  if (row.status === 'quarantined') return 'quarantined';
  if (row.parse_status === 'failed') return 'failed';
  switch (row.dispatch_status) {
    case 'no_order_match':
      return 'needs_match';
    case 'dispatched_with_review':
      return 'needs_review';
    case 'failed':
      return 'failed';
    case 'logged_no_dispatch':
      return 'type_tagged';
    default:
      return null;
  }
}

// ---- List query ---------------------------------------------------

/**
 * Fetch the review queue list for a clinic. Always excludes
 * dismissed rows; always excludes successfully-dispatched rows
 * (`dispatched` / `duplicate_skipped`); always excludes rows that
 * the parser explicitly skipped or hasn't reached yet
 * (`parse_status='skipped'` / `'pending'`).
 *
 * The category filter narrows further to a specific status chip;
 * null means "all queue-eligible rows."
 *
 * Vendor filter is applied client-side after fetch since
 * `parsed_payload->vendor->>value` requires a JSON traversal
 * that PostgREST doesn't filter natively. Acceptable at pilot
 * scale; revisit if queue volume grows.
 */
export async function listReviewQueue(
  clinicId: string,
  filters: QueueFilters,
): Promise<ReviewQueueRow[]> {
  let query = sb
    .from('inbound_emails')
    .select(LIST_COLUMNS)
    .eq('clinic_id', clinicId)
    .is('review_dismissed_at', null)
    .order('parsed_at', { ascending: false, nullsFirst: false });

  // Status filtering. Translate the UI category to the underlying
  // parse_status / dispatch_status predicates. PostgREST's `or()`
  // composition handles the disjunctions in the 'failed' and
  // null-category cases.
  if (filters.category === null) {
    // "All" — every queue-eligible row. parse_status='failed' OR
    // dispatch_status IN (no_order_match, dispatched_with_review,
    // failed, logged_no_dispatch) OR status='quarantined' (#2,
    // Task 5 — quarantined rows never parsed, so they're caught by
    // status, not parse/dispatch_status).
    query = query.or(
      'parse_status.eq.failed,' +
        'dispatch_status.in.(no_order_match,dispatched_with_review,failed,logged_no_dispatch),' +
        'status.eq.quarantined',
    );
  } else if (filters.category === 'failed') {
    query = query.or(
      'parse_status.eq.failed,dispatch_status.eq.failed',
    );
  } else if (filters.category === 'needs_match') {
    query = query.eq('dispatch_status', 'no_order_match');
  } else if (filters.category === 'needs_review') {
    query = query.eq('dispatch_status', 'dispatched_with_review');
  } else if (filters.category === 'type_tagged') {
    query = query.eq('dispatch_status', 'logged_no_dispatch');
  } else if (filters.category === 'quarantined') {
    query = query.eq('status', 'quarantined');
  }

  const { data, error } = await query;
  if (error) {
    throw new Error(`listReviewQueue failed: ${error.message}`);
  }

  // The select returns the typed inbound_emails row shape; ReviewQueueRow is
  // the hand-declared subset this module works with. Narrow through unknown.
  const rows = (data ?? []) as unknown as ReviewQueueRow[];

  // Vendor filter applied client-side. Read from corrected_payload
  // first, fall back to parsed_payload — matches matcher precedence.
  if (filters.vendor !== null) {
    return rows.filter((r) => {
      const effective = r.corrected_payload ?? r.parsed_payload;
      const v = effective?.vendor?.value;
      return v === filters.vendor;
    });
  }
  return rows;
}

/**
 * Distinct vendor names across the visible queue, sorted alphabetically.
 * Drives the vendor dropdown filter. Reads the effective payload
 * (corrected over parsed) so a reviewer who renamed the vendor sees
 * their correction immediately.
 */
export function distinctVendors(rows: ReviewQueueRow[]): string[] {
  const set = new Set<string>();
  for (const r of rows) {
    const effective = r.corrected_payload ?? r.parsed_payload;
    const v = effective?.vendor?.value;
    if (typeof v === 'string' && v.trim().length > 0) {
      set.add(v.trim());
    }
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}

// ---- Detail query -------------------------------------------------

export async function getReviewItem(
  clinicId: string,
  id: string,
): Promise<ReviewQueueDetail | null> {
  const { data, error } = await sb
    .from('inbound_emails')
    .select(DETAIL_COLUMNS)
    .eq('clinic_id', clinicId)
    .eq('id', id)
    .maybeSingle();

  if (error) {
    throw new Error(`getReviewItem failed: ${error.message}`);
  }
  return (data as ReviewQueueDetail | null) ?? null;
}

// ---- Order picker query -------------------------------------------

export interface OpenOrderRow {
  id: string;
  status: string;
  po_number: string | null;
  total_estimated: number;
  sent_at: string | null;
  submitted_at: string;
  vendor_id: string;
  vendor_name: string | null;
}

/**
 * Open orders eligible to be the manual match target on a
 * no_order_match row. Status filter matches the dispatcher's
 * own consideration set (orders.status in 'sent', 'confirmed',
 * 'shipped', 'partial_received') so picking from here is
 * equivalent to what the matcher would have considered.
 */
export async function listOpenOrders(
  clinicId: string,
  vendorFilter?: string | null,
): Promise<OpenOrderRow[]> {
  const { data, error } = await sb
    .from('orders')
    .select(
      'id, status, po_number, total_estimated, sent_at, submitted_at, vendor_id, vendors(name)',
    )
    .eq('clinic_id', clinicId)
    .in('status', ['sent', 'confirmed', 'shipped', 'partial_received'])
    .order('sent_at', { ascending: false, nullsFirst: false });

  if (error) {
    throw new Error(`listOpenOrders failed: ${error.message}`);
  }

  type Row = Omit<OpenOrderRow, 'vendor_name'> & {
    vendors: { name: string } | null;
  };
  const rows: OpenOrderRow[] = ((data ?? []) as Row[]).map((r) => ({
    id: r.id,
    status: r.status,
    po_number: r.po_number,
    total_estimated: r.total_estimated,
    sent_at: r.sent_at,
    submitted_at: r.submitted_at,
    vendor_id: r.vendor_id,
    vendor_name: r.vendors?.name ?? null,
  }));

  if (vendorFilter) {
    const wanted = vendorFilter.trim().toLowerCase();
    return rows.filter(
      (r) => (r.vendor_name ?? '').toLowerCase().includes(wanted),
    );
  }
  return rows;
}

// ---- Mutations ----------------------------------------------------

export interface CommitResult {
  result: string;
  order_id: string | null;
  shipment_id: string | null;
  detail: unknown;
}

/**
 * Commit a reviewer edit and force the matcher to re-dispatch.
 *
 *   1. Write corrected_payload (overwrites any prior correction).
 *   2. Invoke match_and_dispatch_parsed_email(id, p_force=true).
 *
 * Two network round-trips by design — keeping them separate
 * means a successful write followed by a transient RPC failure
 * leaves the correction in place for retry, rather than losing
 * the reviewer's edits.
 *
 * Returns the matcher's outcome row so the UI can branch on
 * 'dispatched' (success), 'dispatched_with_review' (still partial
 * — row stays in queue), 'no_order_match' (still no match), etc.
 */
export async function commitReview(
  clinicId: string,
  id: string,
  correctedPayload: ParsedPayload,
): Promise<CommitResult> {
  const { error: writeError } = await sb
    .from('inbound_emails')
    // corrected_payload is a jsonb column → typed as Json. ParsedPayload is
    // JSON-serializable but lacks Json's index signature; cast at this seam.
    .update({ corrected_payload: correctedPayload as unknown as Json })
    .eq('clinic_id', clinicId)
    .eq('id', id);

  if (writeError) {
    throw new Error(`commitReview write failed: ${writeError.message}`);
  }

  const { data, error: rpcError } = await sb.rpc(
    'match_and_dispatch_parsed_email',
    { p_inbound_email_id: id, p_force: true },
  );

  if (rpcError) {
    throw new Error(`commitReview dispatch failed: ${rpcError.message}`);
  }

  // The RPC returns a single row via `returns table (...)`. Supabase
  // surfaces that as an array; we take the first element.
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) {
    throw new Error('commitReview dispatch returned no result row.');
  }

  return row as CommitResult;
}

/**
 * Stamp review_dismissed_at = now() on a row. Removes it from
 * future queue queries. No-op safe — calling on an already-dismissed
 * row just rewrites the same field.
 */
export async function dismissReview(
  clinicId: string,
  id: string,
): Promise<void> {
  const { error } = await sb
    .from('inbound_emails')
    .update({ review_dismissed_at: new Date().toISOString() })
    .eq('clinic_id', clinicId)
    .eq('id', id);

  if (error) {
    throw new Error(`dismissReview failed: ${error.message}`);
  }
}

// ---- Display-side helpers -----------------------------------------

const CATEGORY_LABEL: Record<ReviewCategory, string> = {
  needs_match: 'Needs match',
  needs_review: 'Needs review',
  failed: 'Failed',
  type_tagged: 'Type-tagged',
  quarantined: 'Unverified sender',
};

export function categoryLabel(c: ReviewCategory): string {
  return CATEGORY_LABEL[c];
}

const CATEGORY_TONE: Record<
  ReviewCategory,
  { bg: string; text: string; ring: string }
> = {
  needs_match: {
    bg: 'bg-amber-50',
    text: 'text-amber-900',
    ring: 'ring-amber-200',
  },
  needs_review: {
    bg: 'bg-green-50',
    text: 'text-green-900',
    ring: 'ring-green-200',
  },
  failed: {
    bg: 'bg-red-50',
    text: 'text-red-900',
    ring: 'ring-red-200',
  },
  type_tagged: {
    bg: 'bg-violet-50',
    text: 'text-violet-900',
    ring: 'ring-violet-200',
  },
  quarantined: {
    bg: 'bg-red-50',
    text: 'text-red-900',
    ring: 'ring-red-200',
  },
};

export function categoryTone(
  c: ReviewCategory,
): { bg: string; text: string; ring: string } {
  return CATEGORY_TONE[c];
}

/**
 * Effective vendor name to display for a row. Reviewer corrections
 * take precedence, then parser, then "(unknown vendor)".
 */
export function effectiveVendor(row: ReviewQueueRow): string {
  const corrected = row.corrected_payload?.vendor?.value;
  if (typeof corrected === 'string' && corrected.trim()) {
    return corrected.trim();
  }
  const parsed = row.parsed_payload?.vendor?.value;
  if (typeof parsed === 'string' && parsed.trim()) {
    return parsed.trim();
  }
  return '(unknown vendor)';
}

/**
 * Effective email_type value for a row. Same precedence as vendor.
 * Returns null when neither source has a value.
 */
export function effectiveEmailType(row: ReviewQueueRow): string | null {
  const corrected = row.corrected_payload?.email_type?.value;
  if (typeof corrected === 'string' && corrected.trim()) {
    return corrected.trim();
  }
  const parsed = row.parsed_payload?.email_type?.value;
  if (typeof parsed === 'string' && parsed.trim()) {
    return parsed.trim();
  }
  return null;
}

/**
 * Loose check that a session's effective role is sufficient
 * to consume this module's mutations. Reads at call sites stay
 * thin; the resolver is the source of truth — this just
 * documents the contract.
 */
export function requiresRole(): ClinicRole {
  return 'manager';
}
